import { useEffect, useMemo, useCallback } from 'react'
import determineNextLoyaltyTier from 'loyalty/lib/determineNextLoyaltyTier'
import calculateTierRemaining from 'loyalty/lib/calculateTierRemaining'
import { isSnackbarShown, markSnackbarAsShown } from 'loyalty/storage/tierProgressSnackbar'
import { useAppSelector } from 'hooks/reduxHooks'
import { showTierProgressionNotification } from 'loyalty/components/LoyaltyTierProgressionNotification'
import useNavigateTo from 'hooks/useNavigateTo'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'

const TIER_PROGRESS_THRESHOLD = 50

interface TierProgressData {
  nextTier: App.LoyaltyTier
  creditsRemaining: number
  nightsRemaining: number
  expiryDate?: string
}

function checkTierProgress(loyaltyAccount: App.LoyaltyAccount): TierProgressData | undefined {
  const nextTier = determineNextLoyaltyTier(loyaltyAccount.tier)
  if (!nextTier) return undefined

  const remaining = calculateTierRemaining(loyaltyAccount, 'upgrade')
  const creditsRemaining = Math.ceil(remaining.creditsRemaining)
  const nightsRemaining = Math.ceil(remaining.nightsRemaining)

  // Only show if within threshold status credits of next tier
  if (creditsRemaining > TIER_PROGRESS_THRESHOLD) {
    return undefined
  }

  // Don't show if already met both requirements
  if (creditsRemaining <= 0 && nightsRemaining <= 0) {
    return undefined
  }

  return {
    nextTier,
    creditsRemaining,
    nightsRemaining,
    expiryDate: loyaltyAccount.statusReviewDate,
  }
}

interface Params {
  memberId?: string
}

function useLoyaltyTierProgressSnackbar(params: Params) {
  const { memberId } = params
  const navigateTo = useNavigateTo()

  const loyaltyAccountState = useAppSelector(state => state.loyalty?.account)
  const userFirstName = useAppSelector(state => state.auth?.account?.givenName)
  const [loyaltyConfig, isConfigFetching] = useLoyaltyConfig()

  const isInitial = loyaltyAccountState?.initial
  const isFetching = loyaltyAccountState?.fetching
  const loyaltyAccount = loyaltyAccountState?.data

  const tierProgressData = useMemo(() => {
    if (isInitial || isFetching) return undefined
    if (!loyaltyAccount) return undefined

    return checkTierProgress(loyaltyAccount)
  }, [isFetching, isInitial, loyaltyAccount])

  const handleViewProgress = useCallback(() => {
    navigateTo(`/${LOYALTY_LANDING_PAGE}#benefits`)
  }, [navigateTo])

  useEffect(() => {
    if (!memberId) return
    if (!tierProgressData) return
    if (!loyaltyAccount) return

    // Wait for loyalty config to load before showing notification
    if (!loyaltyConfig || isConfigFetching) return

    // Only show once per session for this member
    if (isSnackbarShown(memberId)) return

    // Add a small delay to ensure the notification component's event listener is set up
    const timer = setTimeout(() => {
      showTierProgressionNotification({
        nextTier: tierProgressData.nextTier,
        creditsRemaining: tierProgressData.creditsRemaining,
        nightsRemaining: tierProgressData.nightsRemaining,
        expiryDate: tierProgressData.expiryDate,
        userName: userFirstName,
        onViewProgress: handleViewProgress,
      })

      markSnackbarAsShown(memberId)
    }, 300)

    return () => clearTimeout(timer)
  }, [memberId, tierProgressData, userFirstName, handleViewProgress, loyaltyAccount, loyaltyConfig, isConfigFetching])
}

export default useLoyaltyTierProgressSnackbar
